// src/pages/TechWorkConsole.js
import React, {
  useMemo,
  useState,
  useEffect,
  useCallback,
} from "react";
import { useROStore } from "../context/ROContext";
import { useAuth } from "../context/AuthContext";
import { useROUnread } from "../context/ROUnreadContext";
import ROActivityDrawer from "../components/ro/ROActivityDrawer";
import { RO_LIFECYCLE, RO_STATUS } from "../constants/roStatus";

/* -----------------------------
   Helpers
----------------------------- */
function safeString(v) {
  return typeof v === "string" ? v : v == null ? "" : String(v);
}

function safeNumber(v) {
  const n = typeof v === "number" ? v : v === "" || v == null ? NaN : Number(v);
  return Number.isFinite(n) ? n : null;
}

function normalizeRole(v) {
  const raw = safeString(v).trim().toUpperCase();
  if (!raw) return "";

  if (raw === "TECHNICIAN" || raw === "TECH") return "TECHNICIAN";
  if (raw === "MANAGER" || raw === "SERVICE_MANAGER") return "MANAGER";
  if (raw === "ADMIN" || raw === "STORE_ADMIN") return "ADMIN";
  if (raw === "DISPATCHER") return "DISPATCHER";
  if (raw === "FOREMAN" || raw === "SHOP_FOREMAN") return "FOREMAN";
  if (raw === "ADVISOR" || raw === "SERVICE_ADVISOR") return "ADVISOR";
  if (raw === "PARTS" || raw === "PARTS_ADVISOR" || raw === "PARTS_MANAGER") return raw;
  return raw;
}

function formatShortLocal(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleString([], {
    month: "numeric",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

const DATE_FILTERS = Object.freeze({
  TODAY: "TODAY",
  YESTERDAY: "YESTERDAY",
  THIS_WEEK: "THIS_WEEK",
  THIS_MONTH: "THIS_MONTH",
  CUSTOM: "CUSTOM",
});

function startOfLocalDay(input = new Date()) {
  const d = input instanceof Date ? input : new Date(input);
  const safe = Number.isNaN(d.getTime()) ? new Date() : d;
  return new Date(safe.getFullYear(), safe.getMonth(), safe.getDate());
}

function addDays(input, days) {
  const d = new Date(input);
  d.setDate(d.getDate() + days);
  return d;
}

function startOfLocalWeekMonday(input = new Date()) {
  const d = startOfLocalDay(input);
  const day = d.getDay(); // Sunday = 0
  const diff = day === 0 ? -6 : 1 - day;
  return addDays(d, diff);
}

function dateInputValue(input = new Date()) {
  const d = input instanceof Date ? input : new Date(input);
  const safe = Number.isNaN(d.getTime()) ? new Date() : d;
  const pad = (n) => String(n).padStart(2, "0");
  return `${safe.getFullYear()}-${pad(safe.getMonth() + 1)}-${pad(safe.getDate())}`;
}

function localDateFromInput(value) {
  const s = safeString(value).trim();
  if (!s) return null;

  const [year, month, day] = s.split("-").map((part) => Number(part));
  if (!year || !month || !day) return null;

  return new Date(year, month - 1, day);
}

function getTechDateWindow(filter, customFrom, customTo) {
  const todayStart = startOfLocalDay();
  const tomorrowStart = addDays(todayStart, 1);

  if (filter === DATE_FILTERS.YESTERDAY) {
    const yesterdayStart = addDays(todayStart, -1);
    return {
      start: yesterdayStart,
      end: todayStart,
      label: "Yesterday",
      includeCarryoverActive: false,
    };
  }

  if (filter === DATE_FILTERS.THIS_WEEK) {
    return {
      start: startOfLocalWeekMonday(),
      end: tomorrowStart,
      label: "This Week",
      includeCarryoverActive: false,
    };
  }

  if (filter === DATE_FILTERS.THIS_MONTH) {
    const now = new Date();
    return {
      start: new Date(now.getFullYear(), now.getMonth(), 1),
      end: tomorrowStart,
      label: "This Month",
      includeCarryoverActive: false,
    };
  }

  if (filter === DATE_FILTERS.CUSTOM) {
    const start = localDateFromInput(customFrom);
    const toStart = localDateFromInput(customTo);
    const end = toStart ? addDays(toStart, 1) : null;

    return {
      start,
      end,
      label:
        customFrom && customTo
          ? `${customFrom} to ${customTo}`
          : "Custom Range",
      includeCarryoverActive: false,
    };
  }

  return {
    start: todayStart,
    end: tomorrowStart,
    label: "Today",
    includeCarryoverActive: true,
  };
}

function isWithinDateWindow(input, dateWindow) {
  const t = toMs(input);
  const start = dateWindow?.start?.getTime?.();
  const end = dateWindow?.end?.getTime?.();

  if (!Number.isFinite(t) || t <= 0) return false;
  if (!Number.isFinite(start) || !Number.isFinite(end)) return true;

  return t >= start && t < end;
}

function getCreatedISO(ro) {
  return (
    ro?.timestamps?.createdAt ||
    ro?.timestamps?.openedAt ||
    ro?.timestamps?.inAt ||
    ro?.timestamps?.arrivedAt ||
    ro?.createdAtISO ||
    ro?.created_at_iso ||
    ro?.createdAt ||
    ro?.created_at ||
    ro?.openedAtISO ||
    ro?.opened_at_iso ||
    ro?.openedAt ||
    ro?.opened_at ||
    ro?.inAt ||
    ro?.arrivedAt ||
    ""
  );
}

function getCompletedISO(ro) {
  return (
    ro?.technicianCompletedAtISO ||
    ro?.technician_completed_at_iso ||
    ro?.completedAtISO ||
    ro?.completed_at_iso ||
    ro?.timestamps?.technicianCompletedAt ||
    ro?.timestamps?.technician_completed_at ||
    ro?.timestamps?.completedAt ||
    ro?.timestamps?.completed_at ||
    ro?.completedAt ||
    ro?.completed_at ||
    ro?.updatedAtISO ||
    ro?.updatedAt ||
    ""
  );
}

function getTechDateAnchor(ro) {
  if (isCompletedForTechBucket(ro)) {
    return getCompletedISO(ro);
  }

  return (
    ro?.activeWorkStartedAtISO ||
    ro?.active_work_started_at_iso ||
    ro?.timestamps?.activeWorkStartedAtISO ||
    ro?.timestamps?.activeWorkStartedAt ||
    ro?.timestamps?.workStartAt ||
    ro?.workStartAt ||
    ro?.startedAt ||
    ro?.timestamps?.assignedAt ||
    ro?.assignedAt ||
    getCreatedISO(ro) ||
    ro?.timestamps?.updatedAt ||
    ro?.updatedAt ||
    ""
  );
}

function shouldShowInTechDateWindow(ro, dateWindow) {
  if (!dateWindow?.start || !dateWindow?.end) return true;

  if (
    dateWindow.includeCarryoverActive &&
    !isCompletedForTechBucket(ro)
  ) {
    return true;
  }

  return isWithinDateWindow(getTechDateAnchor(ro), dateWindow);
}

function transportLabel(v) {
  const s = String(v || "").toUpperCase();
  const legacy = String(v || "").toLowerCase();

  if (s === "WAITER" || legacy === "waiter") return "Waiter";
  if (s === "LOANER" || legacy === "loaner") return "Loaner";
  if (s === "SHUTTLE" || legacy === "shuttle") return "Shuttle";
  if (s === "OTHER" || legacy === "other") return "Other";
  return "";
}

function getTag(ro) {
  const raw =
    ro?.tagNumber ??
    ro?.tag_number ??
    ro?.tag ??
    ro?.keyTag ??
    ro?.key_tag ??
    ro?.tagId ??
    ro?.tag_id ??
    "";
  return safeString(raw).trim();
}

function sameText(a, b) {
  const aa = safeString(a).trim().toLowerCase();
  const bb = safeString(b).trim().toLowerCase();
  return !!aa && !!bb && aa === bb;
}

function getAssignedTechId(ro) {
  return safeString(
    ro?.assignedTechId ??
      ro?.assigned_tech_id ??
      ro?.techId ??
      ro?.tech_id ??
      ro?.technicianId ??
      ro?.technician_id ??
      ""
  ).trim();
}

function getAssignedTechName(ro) {
  return safeString(
    ro?.assignedTechName ??
      ro?.assigned_tech_name ??
      ro?.techName ??
      ro?.tech_name ??
      ro?.technicianName ??
      ro?.technician_name ??
      ""
  ).trim();
}

function findTechForActor(techs, actorId, actorName) {
  const list = Array.isArray(techs) ? techs : [];
  const id = safeString(actorId).trim();
  const name = safeString(actorName).trim();

  return (
    list.find((t) => sameText(t?.id, id)) ||
    list.find((t) => sameText(t?.appUserId, id)) ||
    list.find((t) => sameText(t?.userId, id)) ||
    list.find((t) => sameText(t?.name, name)) ||
    null
  );
}

function roBelongsToTech(ro, selectedTechId, selectedTechName, actor) {
  const assignedId = getAssignedTechId(ro);
  const assignedName = getAssignedTechName(ro);

  const selectedId = safeString(selectedTechId).trim();
  const selectedName = safeString(selectedTechName).trim();
  const actorId = safeString(actor?.id || actor?.userId || actor?.appUserId).trim();
  const actorName = safeString(actor?.name).trim();

  if (assignedId && selectedId && assignedId === selectedId) return true;
  if (assignedId && actorId && assignedId === actorId) return true;
  if (assignedName && selectedName && sameText(assignedName, selectedName)) return true;
  if (assignedName && actorName && sameText(assignedName, actorName)) return true;

  return false;
}

function toMs(value) {
  const raw = safeString(value).trim();
  if (!raw) return 0;
  const d = new Date(raw);
  const t = d.getTime();
  return Number.isFinite(t) ? t : 0;
}

function isWorkActive(ro) {
  const activeStart =
    safeString(
      ro?.activeWorkStartedAtISO ||
        ro?.active_work_started_at_iso ||
        ro?.timestamps?.activeWorkStartedAtISO ||
        ro?.timestamps?.activeWorkStartedAt ||
        ""
    ).trim();

  if (!activeStart) return false;

  const activeStartMs = toMs(activeStart);
  if (!activeStartMs) return false;

  const stoppedAt =
    safeString(
      ro?.lastStoppedAtISO ||
        ro?.last_stopped_at_iso ||
        ro?.workEndedAtISO ||
        ro?.timestamps?.lastStoppedAtISO ||
        ro?.timestamps?.stoppedAt ||
        ro?.timestamps?.workEndAt ||
        ro?.stoppedAt ||
        ro?.stopped_at ||
        ""
    ).trim();

  if (!stoppedAt) return true;

  const stoppedMs = toMs(stoppedAt);

  // If the latest Start Work is newer than the latest Stop Work,
  // the RO is actively running. This is what preserves the timer
  // after refresh/navigation.
  if (!stoppedMs) return true;

  return activeStartMs > stoppedMs;
}

function hasStoppedWork(ro) {
  if (Array.isArray(ro?.timestamps?.workSessions) && ro.timestamps.workSessions.length > 0) {
    return true;
  }

  if (safeString(ro?.lastStoppedAtISO || ro?.timestamps?.stoppedAt || ro?.timestamps?.workEndAt).trim()) {
    return true;
  }

  if (safeNumber(ro?.totalWorkSeconds) && safeNumber(ro?.totalWorkSeconds) > 0) {
    return true;
  }

  return false;
}

function currentISO() {
  return new Date().toISOString();
}

function pillBase() {
  return {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 6,
    padding: "3px 8px",
    borderRadius: 999,
    fontSize: 10.5,
    fontWeight: 900,
    border: "1px solid #e5e7eb",
    background: "#fff",
    whiteSpace: "nowrap",
    lineHeight: 1.05,
  };
}

function subtlePillStyle() {
  return {
    ...pillBase(),
    borderColor: "#cbd5e1",
    background: "#f8fafc",
    color: "#0f172a",
  };
}

function transportPillStyle(v) {
  const s = String(v || "").toUpperCase();
  const lower = String(v || "").toLowerCase();
  const base = pillBase();

  if (s === "WAITER" || lower === "waiter") {
    return {
      ...base,
      borderColor: "#fecaca",
      background: "#fef2f2",
      color: "#991b1b",
    };
  }
  if (s === "LOANER" || lower === "loaner") {
    return {
      ...base,
      borderColor: "#bfdbfe",
      background: "#eff6ff",
      color: "#1d4ed8",
    };
  }
  if (s === "SHUTTLE" || lower === "shuttle") {
    return {
      ...base,
      borderColor: "#c7d2fe",
      background: "#eef2ff",
      color: "#3730a3",
    };
  }
  if (s === "OTHER" || lower === "other") {
    return {
      ...base,
      borderColor: "#cbd5e1",
      background: "#e5e7eb",
      color: "#334155",
    };
  }

  return null;
}

function statusPillStyle(kind) {
  const base = pillBase();

  if (kind === "AVAILABLE") {
    return {
      ...base,
      borderColor: "#86efac",
      background: "#dcfce7",
      color: "#166534",
    };
  }
  if (kind === "ASSIGNED") {
    return {
      ...base,
      borderColor: "#cbd5e1",
      background: "#f8fafc",
      color: "#0f172a",
    };
  }
  if (kind === "WAITING PARTS") {
    return {
      ...base,
      borderColor: "#fdba74",
      background: "#ffedd5",
      color: "#9a3412",
    };
  }
  if (kind === "IN PROGRESS") {
    return {
      ...base,
      borderColor: "#93c5fd",
      background: "#dbeafe",
      color: "#1e40af",
    };
  }
  if (kind === "COMPLETED") {
    return {
      ...base,
      borderColor: "#cbd5e1",
      background: "#f1f5f9",
      color: "#334155",
    };
  }
  if (kind === "ARCHIVED") {
    return {
      ...base,
      borderColor: "#cbd5e1",
      background: "#e5e7eb",
      color: "#334155",
    };
  }

  return {
    ...base,
    borderColor: "#cbd5e1",
    background: "#f8fafc",
    color: "#0f172a",
  };
}

function normalizeLifecycle(lc) {
  const s = String(lc || "").toUpperCase();
  if (s === "WAITING_ON_PARTS") return RO_LIFECYCLE.WAITING_FOR_PARTS;
  if (s === "WAITING_FOR_PARTS") return RO_LIFECYCLE.WAITING_FOR_PARTS;
  if (s === "WAITING_FOR_ASSIGNMENT") return RO_LIFECYCLE.WAITING_FOR_ASSIGNMENT;
  if (s === "IN_PROGRESS") return RO_LIFECYCLE.IN_PROGRESS;
  if (s === "COMPLETED") return RO_LIFECYCLE.COMPLETED;
  if (s === "OPEN") return RO_LIFECYCLE.OPEN;
  return s || RO_LIFECYCLE.OPEN;
}

function lifecycleLabel(ro, archivedKeyUpper) {
  const st = String(ro?.status || "").toUpperCase();
  if (st === archivedKeyUpper) return "ARCHIVED";

  const lc = normalizeLifecycle(ro?.lifecycle || ro?.lifeCycle);

  if (lc === RO_LIFECYCLE.WAITING_FOR_ASSIGNMENT) return "AVAILABLE";
  if (lc === RO_LIFECYCLE.WAITING_FOR_PARTS) return "WAITING PARTS";
  if (lc === RO_LIFECYCLE.OPEN) return "ASSIGNED";
  if (lc === RO_LIFECYCLE.IN_PROGRESS) return "IN PROGRESS";
  if (lc === RO_LIFECYCLE.COMPLETED) return "COMPLETED";

  return String(lc || "OPEN").replaceAll("_", " ");
}

function lifecyclePillStyle(ro, archivedKeyUpper) {
  return statusPillStyle(lifecycleLabel(ro, archivedKeyUpper));
}

function partsStatusLabel(ps) {
  const s = String(ps || "").toUpperCase();
  if (s === "PULLED") return "Pulled";
  if (s === "ORDERED") return "Ordered";
  if (s === "ARRIVED") return "Arrived";
  if (s === "QUOTED") return "Quoted";
  if (s === "NEEDED") return "Parts Needed";
  if (s === "NONE" || !s) return "";
  return s;
}

function partsStatusPillStyle(ps) {
  const s = String(ps || "").toUpperCase();
  const base = pillBase();

  if (s === "PULLED") {
    return {
      ...base,
      borderColor: "#86efac",
      background: "#dcfce7",
      color: "#166534",
    };
  }
  if (s === "ORDERED") {
    return {
      ...base,
      borderColor: "#fdba74",
      background: "#ffedd5",
      color: "#9a3412",
    };
  }
  if (s === "ARRIVED") {
    return {
      ...base,
      borderColor: "#93c5fd",
      background: "#dbeafe",
      color: "#1e40af",
    };
  }
  if (s === "QUOTED") {
    return {
      ...base,
      borderColor: "#c4b5fd",
      background: "#ede9fe",
      color: "#5b21b6",
    };
  }
  if (s === "NEEDED") {
    return {
      ...base,
      borderColor: "#fecaca",
      background: "#fff1f2",
      color: "#9f1239",
    };
  }

  return null;
}

function requestPillStyle(kind) {
  const k = String(kind || "").toUpperCase();
  const base = pillBase();

  if (k === "NEED_PARTS") {
    return {
      ...base,
      borderColor: "#93c5fd",
      background: "#dbeafe",
      color: "#1e40af",
    };
  }
  if (k === "NEED_PRICE") {
    return {
      ...base,
      borderColor: "#fdba74",
      background: "#ffedd5",
      color: "#9a3412",
    };
  }
  return null;
}

function requestPillText(kind) {
  const k = String(kind || "").toUpperCase();
  if (k === "NEED_PARTS") return "Parts Needed";
  if (k === "NEED_PRICE") return "Quote Requested";
  return "";
}

function metricCardStyle(tone = "default") {
  const tones = {
    default: { border: "#e5e7eb", background: "#ffffff" },
    blue: { border: "#bfdbfe", background: "#eff6ff" },
    green: { border: "#bbf7d0", background: "#f0fdf4" },
    orange: { border: "#fed7aa", background: "#fff7ed" },
    red: { border: "#fecaca", background: "#fff7f7" },
  };

  const t = tones[tone] || tones.default;

  return {
    background: t.background,
    border: `1px solid ${t.border}`,
    borderRadius: 14,
    padding: 11,
    minHeight: 62,
    boxShadow: "0 6px 18px rgba(15,23,42,0.04)",
  };
}

function actionBtnStyle({ primary = false, disabled = false, tone = "default" } = {}) {
  let border = "#d1d5db";
  let background = "#ffffff";
  let color = "#111827";

  if (primary) {
    border = "#111827";
    background = "#111827";
    color = "#ffffff";
  } else if (tone === "blue") {
    border = "#93c5fd";
    background = "#eff6ff";
    color = "#1d4ed8";
  } else if (tone === "orange") {
    border = "#fdba74";
    background = "#fff7ed";
    color = "#9a3412";
  }

  if (disabled) {
    background = "#f9fafb";
    color = "#9ca3af";
    border = "#e5e7eb";
  }

  return {
    minHeight: 27,
    padding: "4px 8px",
    borderRadius: 8,
    border: `1px solid ${border}`,
    background,
    color,
    cursor: disabled ? "not-allowed" : "pointer",
    fontWeight: 900,
    fontSize: 11,
    whiteSpace: "nowrap",
  };
}

function sectionCardStyle({ tone = "default" } = {}) {
  const tones = {
    default: { border: "#e5e7eb", background: "#ffffff", accent: "#cbd5e1" },
    active: { border: "#bfdbfe", background: "#f8fbff", accent: "#3b82f6" },
    waiting: { border: "#fed7aa", background: "#fffaf3", accent: "#f97316" },
    overdue: { border: "#fecaca", background: "#fffafa", accent: "#ef4444" },
    completed: { border: "#bbf7d0", background: "#f8fffb", accent: "#22c55e" },
  };

  const t = tones[tone] || tones.default;

  return {
    border: `1px solid ${t.border}`,
    borderLeft: `4px solid ${t.accent}`,
    borderRadius: 12,
    padding: "7px 9px",
    background: t.background,
    boxShadow: "0 2px 8px rgba(15,23,42,0.02)",
  };
}
function StatCard({ label, value, sub, tone = "default" }) {
  return (
    <div style={metricCardStyle(tone)}>
      <div
        style={{
          fontSize: 10.2,
          fontWeight: 850,
          letterSpacing: "0.05em",
          textTransform: "uppercase",
          color: "rgba(15,23,42,0.48)",
        }}
      >
        {label}
      </div>
      <div
        style={{
          fontSize: 20,
          fontWeight: 800,
          marginTop: 3,
          lineHeight: 1,
          color: "rgba(15,23,42,0.92)",
          letterSpacing: "-0.025em",
        }}
      >
        {value}
      </div>
      <div
        style={{
          fontSize: 10.8,
          color: "rgba(15,23,42,0.55)",
          marginTop: 5,
          lineHeight: 1.28,
        }}
      >
        {sub}
      </div>
    </div>
  );
}

function getPromiseMs(ro) {
  const raw = ro?.promiseTimeISO;
  if (!raw) return Number.POSITIVE_INFINITY;
  const d = new Date(raw);
  if (Number.isNaN(d.getTime())) return Number.POSITIVE_INFINITY;
  return d.getTime();
}

function isWaiter(ro) {
  const t = String(ro?.transportType ?? ro?.customerType ?? "").toUpperCase();
  return t === "WAITER";
}

function isPromiseOverdue(ro) {
  const ms = getPromiseMs(ro);
  return Number.isFinite(ms) && ms < Date.now();
}

function isCompletedForTechBucket(ro) {
  const status = safeString(ro?.status).trim().toUpperCase();
  const lifecycle = normalizeLifecycle(ro?.lifecycle || ro?.lifeCycle);

  return (
    status === String(RO_STATUS?.COMPLETED || "COMPLETED").toUpperCase() ||
    lifecycle === RO_LIFECYCLE.COMPLETED
  );
}

function getPriorityRank(ro, archivedKeyUpper) {
  const lcText = lifecycleLabel(ro, archivedKeyUpper);
  const waiter = isWaiter(ro);
  const overdue = isPromiseOverdue(ro);

  if (waiter && overdue) return 1;
  if (overdue) return 2;
  if (lcText === "IN PROGRESS") return 3;
  if (lcText === "WAITING PARTS") return 4;
  if (waiter) return 5;
  if (lcText === "ASSIGNED" || lcText === "AVAILABLE") return 6;
  return 7;
}

function extractLatestNote(notes) {
  const raw = safeString(notes).trim();
  if (!raw) return "";

  const blocks = raw
    .split(/\n\s*\n/)
    .map((part) => safeString(part).trim())
    .filter(Boolean);

  if (!blocks.length) return "";

  const latestBlock = blocks[blocks.length - 1];
  const lines = latestBlock
    .split("\n")
    .map((line) => safeString(line).trim())
    .filter(Boolean);

  if (lines.length <= 1) return lines[0] || "";
  return lines.slice(1).join(" ");
}

/* -----------------------------
   Component
----------------------------- */
export default function TechWorkConsole() {
  const store = useROStore();
  const auth = useAuth();
  const {
    hasUnread,
    isHydrated: unreadHydrated,
    storeId: unreadStoreId,
  } = useROUnread();

  const startWorkFn = store?.startWork;
  const endWorkFn = store?.endWork;
  const stopWorkFn = store?.stopWork;
  const completeWorkFn = store?.completeWork;

  const updateROFn = store?.updateRO;

  const requestPartsActionFn = store?.requestPartsAction;
  const requestPartsFn = store?.requestParts;

  const ARCHIVED_KEY_UPPER = useMemo(
    () => String(RO_STATUS?.ARCHIVED || "ARCHIVED").toUpperCase(),
    []
  );
  const COMPLETED_LC_KEY_UPPER = useMemo(
    () => String(RO_LIFECYCLE?.COMPLETED || "COMPLETED").toUpperCase(),
    []
  );

  const ros = useMemo(() => {
    const a = Array.isArray(store?.repairOrders) ? store.repairOrders : null;
    const b = Array.isArray(store?.ros) ? store.ros : null;
    return a || b || [];
  }, [store?.repairOrders, store?.ros]);

  const techs = useMemo(() => {
    const a = Array.isArray(store?.techs) ? store.techs : null;
    const b = Array.isArray(store?.technicians) ? store.technicians : null;
    return a || b || [];
  }, [store?.techs, store?.technicians]);

  const computedWip = useMemo(() => {
    const arr = Array.isArray(ros) ? ros : [];
    const isOpenFn = typeof store?.isOpenRO === "function" ? store.isOpenRO : null;

    if (isOpenFn) return arr.filter((r) => isOpenFn(r));

    return arr.filter((r) => {
      const lc = String(r?.lifecycle || r?.lifeCycle || "").toUpperCase();
      const st = String(r?.status || "").toUpperCase();
      return lc !== COMPLETED_LC_KEY_UPPER && st !== ARCHIVED_KEY_UPPER;
    });
  }, [ros, store?.isOpenRO, COMPLETED_LC_KEY_UPPER, ARCHIVED_KEY_UPPER]);

  const wipSource = useMemo(() => {
    const w = Array.isArray(store?.wipROs) ? store.wipROs : null;
    return w && w.length > 0 ? w : computedWip;
  }, [store?.wipROs, computedWip]);

  const currentUser = useMemo(() => {
    return store?.currentUser || { id: "", name: "User", role: "user" };
  }, [store?.currentUser]);

  const actorRoleKey = normalizeRole(currentUser?.role);
  const actorRole = actorRoleKey.toLowerCase();
  const actorName = safeString(currentUser?.name || "User");
  const actorId = safeString(currentUser?.id || "");

  const isManager =
    actorRoleKey === "MANAGER" ||
    actorRoleKey === "ADMIN" ||
    actorRoleKey === "FOREMAN" ||
    actorRoleKey === "DISPATCHER";

  const isTech = actorRoleKey === "TECHNICIAN";

  const initialTechId = useMemo(() => {
    if (isTech) {
      const matched = findTechForActor(techs, actorId, actorName);
      return safeString(matched?.id || actorId).trim();
    }

    return safeString(techs?.[0]?.id) || "";
  }, [isTech, actorId, actorName, techs]);

  const [selectedTechId, setSelectedTechId] = useState(initialTechId);
  const [search, setSearch] = useState("");
  const [dateFilter, setDateFilter] = useState(DATE_FILTERS.TODAY);
  const [customFromDate, setCustomFromDate] = useState(() => dateInputValue());
  const [customToDate, setCustomToDate] = useState(() => dateInputValue());

  const [requestModalROId, setRequestModalROId] = useState(null);
  const [requestModalType, setRequestModalType] = useState("NEED_PARTS");
  const [requestNote, setRequestNote] = useState("");

  const [completeModalROId, setCompleteModalROId] = useState(null);
  const [flaggedHoursInput, setFlaggedHoursInput] = useState("");

  const [localWorkActiveByRO, setLocalWorkActiveByRO] = useState({});
  const [localStoppedByRO, setLocalStoppedByRO] = useState({});

  const [threadROId, setThreadROId] = useState(null);
  const currentUnreadStoreId = safeString(
    auth?.effectiveStoreId || auth?.activeStoreId
  ).trim();
  const unreadStoreMatches =
    !!currentUnreadStoreId &&
    safeString(unreadStoreId).trim() === currentUnreadStoreId;

  useEffect(() => {
    if (isTech) {
      const matched = findTechForActor(techs, actorId, actorName);
      setSelectedTechId(safeString(matched?.id || actorId).trim());
    }
  }, [isTech, actorId, actorName, techs]);

  useEffect(() => {
    if (!selectedTechId && initialTechId) {
      setSelectedTechId(initialTechId);
    }
  }, [selectedTechId, initialTechId]);

  const onTechChange = useCallback((nextTechId) => {
    setSelectedTechId(nextTechId);
  }, []);

  const selectedTechName = useMemo(() => {
    const selected =
      techs.find((t) => safeString(t.id) === safeString(selectedTechId)) ||
      findTechForActor(techs, actorId, actorName);

    return safeString(selected?.name || actorName).trim() || "Technician";
  }, [techs, selectedTechId, actorId, actorName]);

  const dateWindow = useMemo(
    () => getTechDateWindow(dateFilter, customFromDate, customToDate),
    [dateFilter, customFromDate, customToDate]
  );

  const myROs = useMemo(() => {
    const q = (search || "").trim().toLowerCase();

    const base = (Array.isArray(wipSource) ? wipSource : [])
      .filter((r) => roBelongsToTech(r, selectedTechId, selectedTechName, currentUser))
      .filter((r) => shouldShowInTechDateWindow(r, dateWindow));

    const filtered = !q
      ? base
      : base.filter((r) => {
          const roNum = String(r?.roNumber ?? "").toLowerCase();
          const tag = String(
            r?.tagNumber ??
              r?.tag ??
              r?.keyTag ??
              r?.key_tag ??
              r?.tagId ??
              r?.tag_id ??
              ""
          ).toLowerCase();
          const cust = String(r?.customerName ?? "").toLowerCase();
          const veh = String(r?.vehicle ?? "").toLowerCase();
          const adv = String(r?.advisorName ?? "").toLowerCase();
          const notes = String(r?.notes ?? "").toLowerCase();
          const transport = String(r?.transportType ?? r?.customerType ?? "").toLowerCase();
          const ps = String(r?.partsStatus ?? "").toLowerCase();
          const reqType = String(
            r?.partsRequestType ?? r?.partsActionRequestedType ?? ""
          ).toLowerCase();
          const reqNote = String(r?.partsRequestNote ?? r?.partsActionNote ?? "").toLowerCase();
          const created = formatShortLocal(getCreatedISO(r)).toLowerCase();
          const completed = formatShortLocal(getCompletedISO(r)).toLowerCase();

          return (
            roNum.includes(q) ||
            tag.includes(q) ||
            cust.includes(q) ||
            veh.includes(q) ||
            adv.includes(q) ||
            notes.includes(q) ||
            transport.includes(q) ||
            ps.includes(q) ||
            reqType.includes(q) ||
            reqNote.includes(q) ||
            created.includes(q) ||
            completed.includes(q)
          );
        });

    return [...filtered].sort((a, b) => {
      const rankA = getPriorityRank(a, ARCHIVED_KEY_UPPER);
      const rankB = getPriorityRank(b, ARCHIVED_KEY_UPPER);
      if (rankA !== rankB) return rankA - rankB;

      const promiseA = getPromiseMs(a);
      const promiseB = getPromiseMs(b);
      if (promiseA !== promiseB) return promiseA - promiseB;

      const roA = Number(a?.roNumber || 0);
      const roB = Number(b?.roNumber || 0);
      return roB - roA;
    });
  }, [
    wipSource,
    selectedTechId,
    selectedTechName,
    currentUser,
    search,
    dateWindow,
    ARCHIVED_KEY_UPPER,
  ]);

  const activeTechROs = useMemo(() => {
    return myROs.filter((ro) => !isCompletedForTechBucket(ro));
  }, [myROs]);

  const completedTechROs = useMemo(() => {
    return myROs.filter((ro) => isCompletedForTechBucket(ro));
  }, [myROs]);

  const threadRO = useMemo(() => {
    if (!threadROId) return null;

    return (
      myROs.find((r) => String(r?.id) === String(threadROId)) ||
      (Array.isArray(ros)
        ? ros.find((r) => String(r?.id) === String(threadROId))
        : null) ||
      null
    );
  }, [threadROId, myROs, ros]);

  const techDisplayRows = useMemo(() => {
    return [
      {
        __type: "header",
        key: "active-header",
        title: "Assigned / Active Work",
        count: activeTechROs.length,
        sub: "Assigned, in progress, waiting parts, or stopped but not completed.",
      },
      ...(activeTechROs.length
        ? activeTechROs
        : [
            {
              __type: "empty",
              key: "active-empty",
              text: "No assigned or active repair orders.",
            },
          ]),
      {
        __type: "header",
        key: "completed-header",
        title: "Recently Completed",
        count: completedTechROs.length,
        sub: "Completed ROs remain visible here for quick reference.",
      },
      ...(completedTechROs.length
        ? completedTechROs
        : [
            {
              __type: "empty",
              key: "completed-empty",
              text: "No recently completed repair orders.",
            },
          ]),
    ];
  }, [activeTechROs, completedTechROs]);

  const stats = useMemo(() => {
    let inProgress = 0;
    let waitingParts = 0;
    let ordered = 0;

    activeTechROs.forEach((ro) => {
      const lcText = lifecycleLabel(ro, ARCHIVED_KEY_UPPER);
      const ps = String(ro.partsStatus || "").toUpperCase();

      if (lcText === "IN PROGRESS") inProgress += 1;
      if (lcText === "WAITING PARTS") waitingParts += 1;
      if (ps === "ORDERED") ordered += 1;
    });

    return {
      assigned: activeTechROs.length,
      inProgress,
      waitingParts,
      ordered,
      completed: completedTechROs.length,
    };
  }, [activeTechROs, completedTechROs, ARCHIVED_KEY_UPPER]);

  const hasUnreadThreadNote = useCallback(
    (ro) => {
      const roId = safeString(ro?.id).trim();
      return !!(unreadHydrated && unreadStoreMatches && roId && hasUnread(roId));
    },
    [hasUnread, unreadHydrated, unreadStoreMatches]
  );

  const openThreadForRo = useCallback(
    (ro) => {
      const roId = safeString(ro?.id).trim();
      if (!roId) return;

      setThreadROId(roId);
    },
    []
  );

  const closeThread = useCallback(() => {
    setThreadROId(null);
  }, []);

  const startWork = useCallback(
    async (id) => {
      if (typeof startWorkFn !== "function") {
        return alert("Missing startWork in ROContext.js");
      }

      try {
        setLocalWorkActiveByRO((m) => ({ ...m, [id]: true }));
        setLocalStoppedByRO((m) => {
          const next = { ...m };
          delete next[id];
          return next;
        });

        await Promise.resolve(startWorkFn(id));
      } catch (err) {
        setLocalWorkActiveByRO((m) => {
          const next = { ...m };
          delete next[id];
          return next;
        });

        console.error(err);
        alert(`Unable to start work. ${safeString(err?.message).slice(0, 220)}`);
      }
    },
    [startWorkFn]
  );

  const endWork = useCallback(
    async (id) => {
      const fn = typeof stopWorkFn === "function" ? stopWorkFn : endWorkFn;

      if (typeof fn !== "function") {
        return alert("Missing stopWork in ROContext.js");
      }

      try {
        setLocalWorkActiveByRO((m) => ({ ...m, [id]: false }));
        setLocalStoppedByRO((m) => ({ ...m, [id]: true }));

        await Promise.resolve(fn(id));
      } catch (err) {
        setLocalWorkActiveByRO((m) => {
          const next = { ...m };
          delete next[id];
          return next;
        });

        setLocalStoppedByRO((m) => {
          const next = { ...m };
          delete next[id];
          return next;
        });

        console.error(err);
        alert(`Unable to stop work. ${safeString(err?.message).slice(0, 220)}`);
      }
    },
    [endWorkFn, stopWorkFn]
  );

  const openCompleteModal = useCallback(
    (roId) => {
      const ro =
        myROs.find((r) => r.id === roId) ||
        (Array.isArray(ros) ? ros : []).find((r) => r.id === roId);
      if (!ro) return;

      const localStopped = !!localStoppedByRO[roId];
      const localActive = Object.prototype.hasOwnProperty.call(localWorkActiveByRO, roId)
        ? !!localWorkActiveByRO[roId]
        : isWorkActive(ro);
      const hasEndedWork = localStopped || (!localActive && hasStoppedWork(ro));

      if (!hasEndedWork) return;

      const existing = ro ? String(ro.flaggedHours ?? "") : "";
      setCompleteModalROId(roId);
      setFlaggedHoursInput(existing && existing !== "0" ? existing : "");
    },
    [myROs, ros, localStoppedByRO, localWorkActiveByRO]
  );

  const confirmComplete = useCallback(() => {
    const ro =
      myROs.find((r) => r.id === completeModalROId) ||
      (Array.isArray(ros) ? ros : []).find((r) => r.id === completeModalROId);
    if (!ro) return;

    const val = safeNumber(flaggedHoursInput);
    if (val == null || val <= 0) {
      return alert("Flagged hours is required and must be greater than 0.");
    }

    const techName =
      techs.find((t) => safeString(t.id) === safeString(selectedTechId))?.name ||
      currentUser?.name ||
      "Technician";

    const payload = {
      flaggedHours: val,
      completedHours: val,
      hoursFlagged: val,
      hoursCompleted: val,
      techId: selectedTechId,
      techName,
    };

    try {
      if (typeof completeWorkFn === "function") {
        completeWorkFn(ro.id, payload);
      } else if (typeof updateROFn === "function") {
        updateROFn(
          ro.id,
          {
            flaggedHours: val,
            completedHours: val,
            status: RO_STATUS.COMPLETED,
            lifecycle: RO_LIFECYCLE.COMPLETED,
            technicianCompletedAtISO: currentISO(),
            timestamps: {
              ...(ro?.timestamps || {}),
              completedAt: currentISO(),
              technicianCompletedAt: currentISO(),
              updatedAt: currentISO(),
            },
          },
          "WORK_COMPLETED",
          {
            roNumber: ro?.roNumber,
            flaggedHours: val,
            completedHours: val,
            techId: selectedTechId,
            techName,
          }
        );
      } else {
        return alert("Missing completeWork/updateRO in ROContext.js");
      }

      setCompleteModalROId(null);
      setFlaggedHoursInput("");
    } catch (err) {
      console.error(err);
      alert(`Unable to complete work. ${safeString(err?.message).slice(0, 220)}`);
    }
  }, [
    myROs,
    ros,
    completeModalROId,
    flaggedHoursInput,
    techs,
    selectedTechId,
    currentUser?.name,
    completeWorkFn,
    updateROFn,
  ]);

  const openRequestModal = useCallback(
    (roId, kind) => {
      if (!(isTech || isManager)) {
        return alert("Only technicians or managers can request Parts Needed / Quote Requested.");
      }
      setRequestModalROId(roId);
      setRequestModalType(kind);
      setRequestNote("");
    },
    [isTech, isManager]
  );

  const sendRequest = useCallback(async () => {
    if (!(isTech || isManager)) {
      return alert("Only technicians or managers can request Parts Needed / Quote Requested.");
    }

    const ro =
      myROs.find((r) => r.id === requestModalROId) ||
      (Array.isArray(ros) ? ros : []).find((r) => r.id === requestModalROId);
    if (!ro) return;

    const techName =
      techs.find((t) => String(t.id) === String(selectedTechId))?.name ||
      actorName ||
      "Technician";

    const note = String(requestNote || "").trim();
    if (!note) return alert("A note is required so Parts knows exactly what to do.");

    const requestTypeEnum = requestModalType === "NEED_PRICE" ? "NEED_PRICE" : "NEED_PARTS";

    try {
      if (typeof requestPartsActionFn === "function") {
        await Promise.resolve(
          requestPartsActionFn(ro.id, note, requestTypeEnum, "technician", techName)
        );
      } else if (typeof updateROFn === "function") {
        await Promise.resolve(
          updateROFn(
            ro.id,
            {
              partsRequestType: requestTypeEnum,
              partsRequestNote: note,
              partsActionStatus: "REVIEW",
              partsStatus: "NONE",
              status: RO_STATUS.WAITING_ON_PARTS || "WAITING_ON_PARTS",
              lifecycle: RO_LIFECYCLE.WAITING_FOR_PARTS || "WAITING_FOR_PARTS",
              timestamps: {
                ...(ro?.timestamps || {}),
                partsRequestedAt: currentISO(),
                updatedAt: currentISO(),
              },
            },
            "PARTS_REQUESTED",
            {
              roNumber: ro?.roNumber,
              partsRequestType: requestTypeEnum,
              notePreview: note.slice(0, 160),
              byRole: "technician",
              byName: techName,
            }
          )
        );
      } else if (typeof requestPartsFn === "function") {
        await Promise.resolve(requestPartsFn(ro.id, requestTypeEnum));
      } else {
        return alert("Missing requestParts/updateRO in ROContext.js");
      }

      alert(
        requestTypeEnum === "NEED_PRICE"
          ? "Quote Requested sent to Parts ✅"
          : "Parts Needed sent to Parts ✅"
      );
      setRequestModalROId(null);
      setRequestNote("");
    } catch (err) {
      console.error(err);
      alert(`Unable to send parts request. ${safeString(err?.message).slice(0, 220)}`);
    }
  }, [
    isTech,
    isManager,
    myROs,
    requestModalROId,
    ros,
    techs,
    selectedTechId,
    actorName,
    requestNote,
    requestModalType,
    requestPartsActionFn,
    requestPartsFn,
    updateROFn,
  ]);

  return (
    <div
      className="ft-page tech-work-page"
      style={{
        padding: 12,
        display: "grid",
        gap: 10,
        maxWidth: 1500,
        margin: "0 auto",
      }}
    >
      <div
        className="ft-pageHeader tech-work-header"
        style={{
          marginBottom: 0,
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          gap: 10,
        }}
      >
        <div>
          <div
            style={{
              fontSize: 10,
              fontWeight: 900,
              letterSpacing: "0.12em",
              textTransform: "uppercase",
              color: "rgba(15,23,42,0.52)",
              marginBottom: 3,
            }}
          >
            Technician Workspace
          </div>
          <div
            className="ft-h1"
            style={{
              margin: 0,
              fontSize: 22,
              lineHeight: 1.02,
              fontWeight: 700,
              letterSpacing: "-0.035em",
              color: "rgba(15,23,42,0.94)",
            }}
          >
            Technician Console
          </div>
          <div
            className="ft-subtle"
            style={{
              margin: "4px 0 0",
              fontSize: 11.5,
              color: "rgba(15,23,42,0.58)",
              lineHeight: 1.32,
            }}
          >
            Assigned work, timer actions, parts requests, completed ROs, and shared RO notes.
          </div>
        </div>

        <div
          className="tech-work-controls"
          style={{
            display: "flex",
            gap: 8,
            alignItems: "center",
            flexWrap: "wrap",
            justifyContent: "flex-end",
          }}
        >
          {isManager ? (
            <select
              className="tech-work-tech-select"
              value={selectedTechId}
              onChange={(e) => onTechChange(e.target.value)}
              style={{
                height: 34,
                padding: "0 10px",
                borderRadius: 9,
                border: "1px solid rgba(15,23,42,0.10)",
                fontSize: 12.2,
                minWidth: 210,
                background: "#fff",
                fontWeight: 650,
                fontFamily: "inherit",
              }}
              title="Management view: select a technician"
            >
              {techs.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.name}
                </option>
              ))}
            </select>
          ) : (
            <div
              style={{
                padding: "8px 12px",
                border: "1px solid #e5e7eb",
                borderRadius: 999,
                background: "#ffffff",
                fontSize: 12,
                fontWeight: 900,
                color: "#111827",
              }}
            >
              {selectedTechName}
            </div>
          )}

          <select
            className="tech-work-date-select"
            value={dateFilter}
            onChange={(e) => setDateFilter(e.target.value)}
            style={{
              height: 34,
              padding: "0 10px",
              borderRadius: 9,
              border: "1px solid rgba(15,23,42,0.10)",
              fontSize: 12.2,
              minWidth: 160,
              background: "#fff",
              fontWeight: 650,
              fontFamily: "inherit",
            }}
            title="Filter technician work by date range"
          >
            <option value={DATE_FILTERS.TODAY}>Date: Today</option>
            <option value={DATE_FILTERS.YESTERDAY}>Date: Yesterday</option>
            <option value={DATE_FILTERS.THIS_WEEK}>Date: This Week</option>
            <option value={DATE_FILTERS.THIS_MONTH}>Date: This Month</option>
            <option value={DATE_FILTERS.CUSTOM}>Date: Custom</option>
          </select>

          <span
            style={{
              display: "inline-flex",
              alignItems: "center",
              minHeight: 24,
              padding: "2px 8px",
              borderRadius: 999,
              border: "1px solid rgba(15,23,42,0.10)",
              background: "rgba(248,250,252,0.94)",
              fontSize: 10.5,
              fontWeight: 900,
              color: "#111827",
              whiteSpace: "nowrap",
            }}
          >
            Showing: {myROs.length}
          </span>

          {dateFilter === DATE_FILTERS.CUSTOM ? (
            <>
              <input
                className="tech-work-custom-date"
                type="date"
                value={customFromDate}
                onChange={(e) => setCustomFromDate(e.target.value)}
                style={{
                  padding: "8px 10px",
                  borderRadius: 10,
                  border: "1px solid #d1d5db",
                  fontSize: 12.5,
                  background: "#fff",
                  fontWeight: 700,
                }}
              />

              <span style={{ fontSize: 11.5, fontWeight: 900, color: "#64748b" }}>
                to
              </span>

              <input
                className="tech-work-custom-date"
                type="date"
                value={customToDate}
                onChange={(e) => setCustomToDate(e.target.value)}
                style={{
                  padding: "8px 10px",
                  borderRadius: 10,
                  border: "1px solid #d1d5db",
                  fontSize: 12.5,
                  background: "#fff",
                  fontWeight: 700,
                }}
              />
            </>
          ) : null}
        </div>
      </div>

      <div
        className="tech-work-metrics"
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(5, minmax(0, 1fr))",
          gap: 8,
        }}
      >
        <StatCard
          label="Assigned / Active"
          value={stats.assigned}
          sub="Not completed."
          tone={stats.assigned ? "blue" : "green"}
        />
        <StatCard
          label="In Progress"
          value={stats.inProgress}
          sub="Work started."
          tone={stats.inProgress ? "blue" : "default"}
        />
        <StatCard
          label="Waiting Parts"
          value={stats.waitingParts}
          sub="On hold."
          tone={stats.waitingParts ? "orange" : "green"}
        />
        <StatCard
          label="Parts Ordered"
          value={stats.ordered}
          sub="Parts ordered."
          tone="default"
        />
        <StatCard
          label="Completed"
          value={stats.completed}
          sub={`Completed for ${dateWindow.label}.`}
          tone={stats.completed ? "green" : "default"}
        />
      </div>

      <div
        className="tech-work-search-card"
        style={{
          background: "#ffffff",
          border: "1px solid #e5e7eb",
          borderRadius: 14,
          padding: 11,
          boxShadow: "0 6px 18px rgba(15,23,42,0.04)",
        }}
      >
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search RO #, tag, customer, vehicle, advisor, notes…"
          style={{
            width: "100%",
            padding: "8px 10px",
            borderRadius: 9,
            border: "1px solid rgba(15,23,42,0.12)",
            fontSize: 12.2,
            background: "#ffffff",
            outline: "none",
          }}
        />
        <div style={{ marginTop: 7, fontSize: 11.5, color: "rgba(15,23,42,0.55)" }}>
          Active work stays visible today. Completed work follows the selected date range:{" "}
          <b style={{ color: "#334155" }}>{dateWindow.label}</b>.
        </div>
      </div>

      {myROs.length === 0 ? (
        <div
          style={{
            padding: 16,
            border: "1px dashed #cbd5e1",
            borderRadius: 14,
            color: "#64748b",
            fontSize: 12.5,
            background: "#ffffff",
          }}
        >
          No repair orders for {selectedTechName} in {dateWindow.label}.
        </div>
      ) : (
        <div style={{ display: "grid", gap: 8 }}>
          {techDisplayRows.map((row) => {
            if (row?.__type === "header") {
              return (
                <div
                  key={row.key}
                  style={{
                    marginTop: row.key === "active-header" ? 0 : 8,
                    padding: "7px 9px",
                    borderRadius: 12,
                    border: "1px solid #e5e7eb",
                    background: "#f8fafc",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 12,
                    flexWrap: "wrap",
                  }}
                >
                  <div>
                    <div
                      style={{
                        fontSize: 12.2,
                        fontWeight: 800,
                        color: "#111827",
                      }}
                    >
                      {row.title}
                    </div>
                    <div style={{ fontSize: 10.8, color: "rgba(15,23,42,0.55)", marginTop: 2 }}>
                      {row.sub}
                    </div>
                  </div>

                  <div
                    style={{
                      padding: "4px 9px",
                      borderRadius: 999,
                      border: "1px solid #cbd5e1",
                      background: "#ffffff",
                      fontSize: 11.5,
                      fontWeight: 900,
                      color: "#334155",
                    }}
                  >
                    {row.count}
                  </div>
                </div>
              );
            }

            if (row?.__type === "empty") {
              return (
                <div
                  key={row.key}
                  style={{
                    padding: 10,
                    border: "1px dashed #cbd5e1",
                    borderRadius: 12,
                    color: "rgba(15,23,42,0.55)",
                    fontSize: 11.5,
                    background: "#ffffff",
                  }}
                >
                  {row.text}
                </div>
              );
            }

            const ro = row;
            const hasLocalActiveOverride = Object.prototype.hasOwnProperty.call(
              localWorkActiveByRO,
              ro.id
            );
            const working = hasLocalActiveOverride
              ? !!localWorkActiveByRO[ro.id]
              : isWorkActive(ro);

            const hasEndedWork =
              !!localStoppedByRO[ro.id] || (!working && hasStoppedWork(ro));

            const canComplete = hasEndedWork;

            const tLabel = transportLabel(ro.transportType ?? ro.customerType);
            const tStyle = transportPillStyle(ro.transportType ?? ro.customerType);

            const ps = String(ro.partsStatus || "").toUpperCase();
            const psLabel = partsStatusLabel(ps);
            const psStyle = partsStatusPillStyle(ps);

            const reqType = String(
              ro.partsRequestType || ro.partsActionRequestedType || ""
            ).toUpperCase();
            const reqPill = reqType ? requestPillStyle(reqType) : null;
            const reqText = reqType ? requestPillText(reqType) : "";

            const tag = getTag(ro);
            const lcText = lifecycleLabel(ro, ARCHIVED_KEY_UPPER);
            const overdue = isPromiseOverdue(ro);
            const latestNote = extractLatestNote(ro.notes);
            const hasUnreadNote = hasUnreadThreadNote(ro);
            const completedBucket = isCompletedForTechBucket(ro);

            const rowTone = completedBucket
              ? "completed"
              : overdue
              ? "overdue"
              : lcText === "WAITING PARTS"
              ? "waiting"
              : working
              ? "active"
              : "default";

            return (
              <div key={ro.id} style={sectionCardStyle({ tone: rowTone })}>
                <div
                  className="tech-work-ro-layout"
                  style={{
                    display: "grid",
                    gridTemplateColumns: "minmax(0, 1fr) auto",
                    gap: 10,
                    alignItems: "center",
                  }}
                >
                  <div style={{ minWidth: 0 }}>
                    <div
                      style={{
                        display: "flex",
                        gap: 6,
                        flexWrap: "wrap",
                        alignItems: "center",
                      }}
                    >
                      <div style={{ fontSize: 13.25, fontWeight: 800, lineHeight: 1 }}>
                        RO #{ro.roNumber || "—"}
                      </div>

                      <span
                        style={subtlePillStyle()}
                        title={tag ? "Key tag number" : "Missing tag number"}
                      >
                        {tag ? `Tag #${tag}` : "Tag Missing"}
                      </span>

                      <span
                        style={lifecyclePillStyle(ro, ARCHIVED_KEY_UPPER)}
                        title="Lifecycle"
                      >
                        {lcText}
                      </span>

                      {tStyle ? <span style={tStyle}>{tLabel || "Other"}</span> : null}
                      {psLabel && psStyle ? <span style={psStyle}>{psLabel}</span> : null}
                      {reqPill && reqText ? <span style={reqPill}>{reqText}</span> : null}

                      {overdue ? (
                        <span
                          style={{
                            ...pillBase(),
                            borderColor: "#fecaca",
                            background: "#fef2f2",
                            color: "#991b1b",
                          }}
                        >
                          Overdue
                        </span>
                      ) : null}
                    </div>

                    <div style={{ marginTop: 4, fontSize: 11.8, color: "rgba(15,23,42,0.74)" }}>
                      <b>{ro.customerName || "—"}</b> — {ro.vehicle || "—"}
                    </div>

                    <div
                      style={{
                        marginTop: 3,
                        display: "flex",
                        gap: 10,
                        rowGap: 3,
                        flexWrap: "wrap",
                        fontSize: 10.8,
                        color: "rgba(15,23,42,0.55)",
                      }}
                    >
                      <span>
                        Advisor:{" "}
                        <b style={{ color: "#374151" }}>
                          {safeString(ro.advisorName).trim() ? ro.advisorName : "—"}
                        </b>
                      </span>

                      <span>
                        Promise:{" "}
                        <b style={{ color: overdue ? "#991b1b" : "#374151" }}>
                          {ro.promiseTimeISO ? formatShortLocal(ro.promiseTimeISO) : "—"}
                        </b>
                      </span>

                      {ro.partsRequestNote || ro.partsActionNote ? (
                        <span>
                          Request:{" "}
                          <b style={{ color: "#374151" }}>
                            {ro.partsRequestNote || ro.partsActionNote}
                          </b>
                        </span>
                      ) : null}
                    </div>

                    {latestNote ? (
                      <div
                        style={{
                          marginTop: 5,
                          padding: "4px 7px",
                          borderRadius: 9,
                          border: "1px solid #e5e7eb",
                          background: "#f8fafc",
                          fontSize: 10.8,
                          color: "#475569",
                          maxWidth: 860,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        <span style={{ fontWeight: 900, color: "#334155" }}>
                          Latest note:
                        </span>{" "}
                        {latestNote}
                      </div>
                    ) : null}
                  </div>

                  <div
                    className="tech-work-action-grid"
                    style={{
                      display: "grid",
                      gridTemplateColumns: "repeat(3, minmax(82px, 1fr))",
                      gap: 6,
                      width: 306,
                      maxWidth: "100%",
                    }}
                  >
                    <button
                      type="button"
                      onClick={() => startWork(ro.id)}
                      disabled={working || completedBucket}
                      style={actionBtnStyle({ disabled: working || completedBucket })}
                      title="Starts timing and sets lifecycle to IN_PROGRESS"
                    >
                      Start
                    </button>

                    <button
                      type="button"
                      onClick={() => endWork(ro.id)}
                      disabled={!working || completedBucket}
                      style={actionBtnStyle({ disabled: !working || completedBucket })}
                      title="Stops timing"
                    >
                      Stop
                    </button>

                    <button
                      type="button"
                      onClick={() => openCompleteModal(ro.id)}
                      disabled={completedBucket || !canComplete}
                      style={actionBtnStyle({
                        primary: true,
                        disabled: completedBucket || !canComplete,
                      })}
                      title={
                        canComplete
                          ? "Flagged hours required to complete"
                          : "Start and stop work before marking completed"
                      }
                    >
                      Complete
                    </button>

                    <button
                      type="button"
                      onClick={() => openRequestModal(ro.id, "NEED_PARTS")}
                      disabled={completedBucket || !(isTech || isManager)}
                      style={actionBtnStyle({
                        disabled: completedBucket || !(isTech || isManager),
                        tone: "blue",
                      })}
                      title="Technician or management"
                    >
                      Parts
                    </button>

                    <button
                      type="button"
                      onClick={() => openRequestModal(ro.id, "NEED_PRICE")}
                      disabled={completedBucket || !(isTech || isManager)}
                      style={actionBtnStyle({
                        disabled: completedBucket || !(isTech || isManager),
                        tone: "orange",
                      })}
                      title="Technician or management"
                    >
                      Quote
                    </button>

                    <button
                      type="button"
                      onClick={() => openThreadForRo(ro)}
                      style={{
                        ...actionBtnStyle({
                          tone: hasUnreadNote ? "orange" : "default",
                        }),
                        position: "relative",
                      }}
                      title={hasUnreadNote ? "Unread note activity" : "Open notes and activity"}
                    >
                      Notes
                      {hasUnreadNote ? (
                        <span
                          style={{
                            position: "absolute",
                            top: -5,
                            right: -5,
                            width: 10,
                            height: 10,
                            borderRadius: 999,
                            background: "#f97316",
                            border: "2px solid #ffffff",
                            boxShadow: "0 0 0 1px rgba(249,115,22,0.18)",
                          }}
                        />
                      ) : null}
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {requestModalROId ? (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.35)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: 16,
            zIndex: 100,
          }}
          onClick={() => setRequestModalROId(null)}
        >
          <div
            style={{
              width: "min(620px, 100%)",
              background: "white",
              borderRadius: 16,
              padding: 16,
              border: "1px solid #e5e7eb",
              boxShadow: "0 24px 60px rgba(0,0,0,0.18)",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ fontWeight: 900, fontSize: 16 }}>
              {requestModalType === "NEED_PRICE" ? "Quote Requested" : "Parts Needed"}
            </div>

            <div style={{ color: "#6b7280", marginTop: 6, fontSize: 12.5 }}>
              Required note: include exact part(s), qty, and details so Parts can act fast.
            </div>

            <textarea
              value={requestNote}
              onChange={(e) => setRequestNote(e.target.value)}
              rows={5}
              style={{
                width: "100%",
                marginTop: 12,
                padding: 10,
                borderRadius: 12,
                border: "1px solid #ddd",
                fontSize: 13,
              }}
              placeholder={
                requestModalType === "NEED_PRICE"
                  ? "Example: Quote front brake pads + rotors. Parts only."
                  : "Example: Need coolant hose + clamp. VIN/PN if available."
              }
            />

            <div
              style={{ display: "flex", justifyContent: "flex-end", gap: 10, marginTop: 12 }}
            >
              <button
                type="button"
                onClick={() => setRequestModalROId(null)}
                style={actionBtnStyle()}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={sendRequest}
                style={actionBtnStyle({ primary: true })}
              >
                Send to Parts
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {completeModalROId ? (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.35)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: 16,
            zIndex: 110,
          }}
          onClick={() => setCompleteModalROId(null)}
        >
          <div
            style={{
              width: "min(520px, 100%)",
              background: "white",
              borderRadius: 16,
              padding: 16,
              border: "1px solid #e5e7eb",
              boxShadow: "0 24px 60px rgba(0,0,0,0.18)",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ fontWeight: 900, fontSize: 16 }}>Flagged Hours Required</div>
            <div style={{ color: "#6b7280", marginTop: 6, fontSize: 12.5 }}>
              Enter the hours flagged for this RO. This is required for Tech Stats and
              productivity reporting.
            </div>

            <div style={{ marginTop: 12 }}>
              <label style={{ fontSize: 12.5, fontWeight: 900, color: "#4b5563" }}>
                Flagged Hours
              </label>
              <input
                value={flaggedHoursInput}
                onChange={(e) => setFlaggedHoursInput(e.target.value)}
                type="number"
                min="0"
                step="0.1"
                placeholder="e.g. 1.5"
                style={{
                  width: "100%",
                  marginTop: 6,
                  padding: "10px 12px",
                  borderRadius: 12,
                  border: "1px solid #ddd",
                  fontSize: 14,
                }}
              />
              <div style={{ marginTop: 8, fontSize: 11.5, color: "#6b7280" }}>
                Must be greater than 0 to complete.
              </div>
            </div>

            <div
              style={{ display: "flex", justifyContent: "flex-end", gap: 10, marginTop: 12 }}
            >
              <button
                type="button"
                onClick={() => setCompleteModalROId(null)}
                style={actionBtnStyle()}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={confirmComplete}
                style={actionBtnStyle({ primary: true })}
              >
                Save & Complete
              </button>
            </div>
          </div>
        </div>
      ) : null}

      <style>
        {`
          @media (max-width: 1100px) {
            .tech-work-page {
              padding: 8px !important;
            }

            .tech-work-header {
              flex-direction: column !important;
              align-items: stretch !important;
            }

            .tech-work-controls {
              width: 100%;
              justify-content: flex-start !important;
            }

            .tech-work-tech-select,
            .tech-work-date-select,
            .tech-work-custom-date {
              min-width: 0 !important;
              flex: 1 1 180px;
            }

            .tech-work-metrics {
              grid-template-columns: repeat(3, minmax(0, 1fr)) !important;
            }

            .tech-work-ro-layout {
              grid-template-columns: 1fr !important;
              align-items: start !important;
            }

            .tech-work-action-grid {
              width: 100% !important;
              grid-template-columns: repeat(3, minmax(0, 1fr)) !important;
            }
          }

          @media (max-width: 900px) {
            .tech-work-page {
              padding: 4px !important;
              gap: 8px !important;
            }

            .tech-work-metrics {
              grid-template-columns: repeat(2, minmax(0, 1fr)) !important;
            }

            .tech-work-search-card {
              padding: 9px !important;
            }
          }

          @media (max-width: 560px) {
            .tech-work-metrics,
            .tech-work-action-grid {
              grid-template-columns: 1fr !important;
            }
          }
        `}
      </style>

      <ROActivityDrawer
        ro={threadRO}
        isOpen={!!threadRO}
        onClose={closeThread}
        currentUser={currentUser}
        actorRole={actorRole}
        actorName={actorName}
        title="Technician RO Thread"
      />
    </div>
  );
}
